import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  useTheme,
  Divider,
  CircularProgress,
  Container,
  Stack,
  Card,
  CardContent,
  Alert,
  Snackbar,
  alpha,
  Slide
} from '@mui/material';
import {
  People as PeopleIcon,
  PersonOutline as InactiveUserIcon,
  PersonAdd as NewUserIcon,
  ViewModule as ContainerIcon,
  Lock as LockerIcon,
  ShoppingCart as OrderIcon,
  CheckCircle as SuccessIcon,
  Cancel as FailedIcon,
  LocalShipping as OccupiedIcon,
  AttachMoney as PaymentIcon,
  Timer as ChargesIcon,
  Block as InactiveIcon,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useLocalization } from '../../contexts/LocalizationContext';
import { dashboardService } from '../../services/api';
import { useNavigate } from 'react-router-dom';


interface MetricTileProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  filter?: string;
}

interface SectionProps {
  title: string;
  metrics: MetricTileProps[];
  icon: React.ReactNode;
}

interface DashboardData {
  users: {
    total: number;
    active: number;
    inactive: number;
    newUsers: number;
  };
  containers: {
    total: number;
    active: number;
    inactive: number;
    // occupied: number;
  };
  lockers: {
    total: number;
    active: number;
    inactive: number;
    occupied: number;
  };
  orders: {
    total: number;
    active: number;
    success: number;
    failed: number;
  };
  payments: {
    lockerCharges: number;
    escrow: number;
    transferred: number;
    refunded: number;
  };
}

const MetricTile: React.FC<MetricTileProps & { navigateTo?: string }> = ( { title, value, icon, color, trend, trendValue, navigateTo, filter
} ) => {
  const navigate = useNavigate();
  return (
    <Card
      sx={ {
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        cursor: navigateTo ? 'pointer' : 'default',
        '&:hover': navigateTo
          ? {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
            transition: 'all 0.3s ease-in-out',
          }
          : {},
      } }
      elevation={ 0 }
      onClick={ () => {
        if ( navigateTo ) {
          navigate( navigateTo, { state: { filter } } );


        }
      } }



    >
      <CardContent sx={ { p: 3 } }>
        <Box
          sx={ {
            position: 'absolute',
            top: 12,
            right: 12,
            p: 1,
            borderRadius: 2,
            backgroundColor: alpha( color, 0.1 ),
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          } }
        >
          { icon }
        </Box>

        <Box sx={ { pr: 6 } }>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={ { fontWeight: 500 } }>
            { title }
          </Typography>
          <Typography variant="h4" component="div" sx={ { color: color, fontWeight: 700, mb: 1 } }>
            { value }
          </Typography>

          {/* { trend && trendValue && (
            <Stack direction="row" alignItems="center" spacing={ 0.5 }>
              { trend === 'up' ? (
                <TrendingUpIcon sx={ { fontSize: 16, color: 'success.main' } } />
              ) : trend === 'down' ? (
                <TrendingDownIcon sx={ { fontSize: 16, color: 'error.main' } } />
              ) : null }
              <Typography
                variant="caption"
                color={
                  trend === 'up'
                    ? 'success.main'
                    : trend === 'down'
                      ? 'error.main'
                      : 'textSecondary'
                }
              >
                { trendValue }
              </Typography>
            </Stack>
          ) } */}
        </Box>
      </CardContent>
    </Card>
  );
}

const DashboardSection: React.FC<SectionProps> = ( { title, metrics, icon } ) => (
  <Box sx={ { mb: 4 } }>
    <Stack direction="row" alignItems="center" spacing={ 2 } sx={ { mb: 3 } }>
      <Box sx={ {
        p: 1,
        borderRadius: 2,
        bgcolor: 'primary.main',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      } }>
        { icon }
      </Box>
      <Typography variant="h5" fontWeight="600" color="text.primary">
        { title }
      </Typography>
    </Stack>
    <Grid container spacing={ 3 }>
      { metrics.map( ( metric, index ) => (
        <Grid item xs={ 12 } sm={ 6 } md={ 3 } key={ index }>
          <MetricTile { ...metric } />
        </Grid>
      ) ) }
    </Grid>
  </Box>
);

export const Dashboard: React.FC = () => {
  const { t } = useLocalization();
  const theme = useTheme();
  const [ dashboardData, setDashboardData ] = useState<DashboardData | null>( null );
  const [ loading, setLoading ] = useState( true );
  const [ error, setError ] = useState<string | null>( null );
  const [ toast, setToast ] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>( {
    open: false,
    message: '',
    severity: 'success'
  } );

  useEffect( () => {
    const fetchDashboardData = async () => {
      try {
        setLoading( true );
        const response = await dashboardService.getDashboardData();
        if ( response.success ) {
          setDashboardData( response.data );
        } else {
          setError( response.message || 'Failed to fetch dashboard data' );
          setToast( {
            open: true,
            message: response.message || 'Failed to fetch dashboard data',
            severity: 'error'
          } );
        }
      } catch ( err ) {
        console.error( 'Error fetching dashboard data:', err );
        setError( 'Failed to fetch dashboard data' );
        setToast( {
          open: true,
          message: 'Failed to fetch dashboard data',
          severity: 'error'
        } );
      } finally {
        setLoading( false );
      }
    };

    fetchDashboardData();
  }, [] );

  const handleCloseToast = () => {
    setToast( { ...toast, open: false } );
  };

  if ( loading ) {
    return (
      <Container maxWidth="lg" sx={ { py: 4 } }>
        <Box sx={ {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2
        } }>
          <CircularProgress size={ 60 } />
          <Typography variant="h6" color="text.secondary">
            Loading dashboard data...
          </Typography>
        </Box>
      </Container>
    );
  }

  if ( error ) {
    return (
      <Container maxWidth="lg" sx={ { py: 4 } }>
        <Alert severity="error" sx={ { mb: 2 } }>
          { error }
        </Alert>
      </Container>
    );
  }

  if ( !dashboardData ) {
    return (
      <Container maxWidth="lg" sx={ { py: 4 } }>
        <Alert severity="info">
          No dashboard data available
        </Alert>
      </Container>
    );
  }

  const sections = [
    {
      title: t.dashboard.sections.users,
      icon: <PeopleIcon sx={ { fontSize: 20 } } />,
      metrics: [
        {
          title: t.dashboard.metrics.allUsers,
          value: dashboardData.users.total.toLocaleString(),
          icon: <PeopleIcon fontSize="small" />,
          color: theme.palette.primary.main,
          trend: 'up' as const,
          trendValue: '+12% this month',
          navigateTo: '/users'
        },
        {
          title: t.dashboard.metrics.activeUsers,
          value: dashboardData.users.active.toLocaleString(),
          icon: <PeopleIcon fontSize="small" />,
          color: theme.palette.success.main,
          trend: 'up' as const,
          trendValue: '+8% this week',
          navigateTo: '/users',
          filter: 'active'
        },
        {
          title: t.dashboard.metrics.inactiveUsers,
          value: dashboardData.users.inactive.toLocaleString(),
          icon: <InactiveUserIcon fontSize="small" />,
          color: theme.palette.error.main,
          trend: 'down' as const,
          trendValue: '-3% this month',
          navigateTo: '/users',
          filter: 'inactive'
        },
        {
          title: t.dashboard.metrics.newUsers,
          value: dashboardData.users.newUsers.toLocaleString(),
          icon: <NewUserIcon fontSize="small" />,
          color: theme.palette.info.main,
          trend: 'up' as const,
          trendValue: '+15% this week',
          navigateTo: '/users'
        },
      ],
    },
    {
      title: t.dashboard.sections.containers,
      icon: <ContainerIcon sx={ { fontSize: 20 } } />,
      metrics: [
        {
          title: t.dashboard.metrics.allContainers,
          value: dashboardData.containers.total.toLocaleString(),
          icon: <ContainerIcon fontSize="small" />,
          color: theme.palette.primary.main,
          trend: 'neutral' as const,
          trendValue: 'No change',
          navigateTo: '/lockers'
        },
        {
          title: t.dashboard.metrics.activeContainers,
          value: dashboardData.containers.active.toLocaleString(),
          icon: <ContainerIcon fontSize="small" />,
          color: theme.palette.success.main,
          trend: 'up' as const,
          trendValue: '+5% this month',
          navigateTo: '/lockers',
          filter: 'active'
        },
        {
          title: t.dashboard.metrics.inactiveContainers,
          value: dashboardData.containers.inactive.toLocaleString(),
          icon: <InactiveIcon fontSize="small" />,
          color: theme.palette.error.main,
          trend: 'down' as const,
          trendValue: '-2% this month',
          navigateTo: '/lockers',
          filter: 'inactive'
        },
      ],
    },
    {
      title: t.dashboard.sections.lockers,
      icon: <LockerIcon sx={ { fontSize: 20 } } />,
      metrics: [
        {
          title: t.dashboard.metrics.allLockers,
          value: dashboardData.lockers.total.toLocaleString(),
          icon: <LockerIcon fontSize="small" />,
          color: theme.palette.primary.main,
          trend: 'neutral' as const,
          trendValue: 'No change'
        },
        {
          title: t.dashboard.metrics.activeLockers,
          value: dashboardData.lockers.active.toLocaleString(),
          icon: <LockerIcon fontSize="small" />,
          color: theme.palette.success.main,
          trend: 'up' as const,
          trendValue: '+7% this month'
        },
        {
          title: t.dashboard.metrics.inactiveLockers,
          value: dashboardData.lockers.inactive.toLocaleString(),
          icon: <InactiveIcon fontSize="small" />,
          color: theme.palette.error.main,
          trend: 'down' as const,
          trendValue: '-1% this month'
        },
        {
          title: t.dashboard.metrics.occupiedLockers,
          value: dashboardData.lockers.occupied.toLocaleString(),
          icon: <OccupiedIcon fontSize="small" />,
          color: theme.palette.warning.main,
          trend: 'up' as const,
          trendValue: '+12% this week'
        },
      ],
    },
    {
      title: t.dashboard.sections.orders,
      icon: <OrderIcon sx={ { fontSize: 20 } } />,
      metrics: [
        {
          title: t.dashboard.metrics.totalOrders,
          value: dashboardData.orders.total.toLocaleString(),
          icon: <OrderIcon fontSize="small" />,
          color: theme.palette.primary.main,
          trend: 'up' as const,
          trendValue: '+18% this month',
          navigateTo: '/orders'
        },
        {
          title: t.dashboard.metrics.activeOrders,
          value: dashboardData.orders.active.toLocaleString(),
          icon: <OrderIcon fontSize="small" />,
          color: theme.palette.info.main,
          trend: 'up' as const,
          trendValue: '+25% this week',
          navigateTo: '/orders',
          filter: "active"

        },
        {
          title: t.dashboard.metrics.successfulOrders,
          value: dashboardData.orders.success.toLocaleString(),
          icon: <SuccessIcon fontSize="small" />,
          color: theme.palette.success.main,
          trend: 'up' as const,
          trendValue: '+15% this month',
          navigateTo: '/orders',
          filter: 'successfull'

        },
        {
          title: t.dashboard.metrics.failedOrders,
          value: dashboardData.orders.failed.toLocaleString(),
          icon: <FailedIcon fontSize="small" />,
          color: theme.palette.error.main,
          trend: 'down' as const,
          trendValue: '-8% this month',
          navigateTo: '/orders',
          filter: 'failed'
        },
      ],
    },
    {
      title: t.dashboard.sections.payments,
      icon: <AccountBalanceIcon sx={ { fontSize: 20 } } />,
      metrics: [
        {
          title: t.dashboard.metrics.lockerCharges,
          value: `$${ dashboardData.payments.lockerCharges.toLocaleString() }`,
          icon: <ChargesIcon fontSize="small" />,
          color: theme.palette.info.main,
          trend: 'up' as const,
          trendValue: '+22% this month',
          navigateTo: '/payment-history',
          filter: 'Locker_Charges'
        },
        {
          title: t.dashboard.metrics.escrowPayments,
          value: `$${ dashboardData.payments.escrow.toLocaleString() }`,
          icon: <PaymentIcon fontSize="small" />,
          color: theme.palette.warning.main,
          trend: 'up' as const,
          trendValue: '+30% this month',
          navigateTo: '/payment-history',
          filter: 'Payment_In_Escrow',
        },
        {
          title: t.dashboard.metrics.sellerPayments,
          value: `$${ dashboardData.payments.transferred.toLocaleString() }`,
          icon: <PaymentIcon fontSize="small" />,
          color: theme.palette.success.main,
          trend: 'up' as const,
          trendValue: '+28% this month',
          navigateTo: '/payment-history',
          filter: 'PAYMENT_TO_SELLER'
        },
        {
          title: t.dashboard.metrics.buyerRefunds,
          value: `$${ dashboardData.payments.refunded.toLocaleString() }`,
          icon: <PaymentIcon fontSize="small" />,
          color: theme.palette.error.main,
          trend: 'down' as const,
          trendValue: '-5% this month',
          navigateTo: '/payment-history',
          filter: 'REFUND_TO_BUYER'
        },
      ],
    },
  ];

  return (
    <Container maxWidth={ false } sx={ { py: 4, width: '100%' } }>
      <Slide direction="down" in={ true } timeout={ 600 }>
        <Box>
          {/* Header Section */ }
          <Box sx={ { mb: 4 } }>
            <Stack direction="row" alignItems="center" spacing={ 2 } sx={ { mb: 2 } }>
              <Box sx={ {
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              } }>
                <DashboardIcon sx={ { fontSize: 28 } } />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  { t.common.dashboard }
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Overview of your platform's key metrics and performance
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Dashboard Sections */ }
          { sections.map( ( section, index ) => (
            <React.Fragment key={ index }>
              <DashboardSection { ...section } />
              { index < sections.length - 1 && (
                <Divider sx={ { my: 4, opacity: 0.3 } } />
              ) }
            </React.Fragment>
          ) ) }
        </Box>
      </Slide>

      <Snackbar
        open={ toast.open }
        autoHideDuration={ 6000 }
        onClose={ handleCloseToast }
        anchorOrigin={ { vertical: 'top', horizontal: 'right' } }
      >
        <Alert onClose={ handleCloseToast } severity={ toast.severity } sx={ { width: '100%' } }>
          { toast.message }
        </Alert>
      </Snackbar>
    </Container>
  );
}; 